import { Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { EmailNotificationField } from "./notification-status"
import type { PersonalNotificationSettingsProps } from "./types"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero } from "@/lib/zero"
import { docsDigestToggleTitle } from "@/screens/app/helpCenter/docs-proposal-cadence"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import {
	buildToggleAllPayload,
	EMAIL_NOTIFICATION_FIELDS,
	isEmailNotificationsEnabled,
} from "./notification-status"

export function PersonalEmailNotificationSection({
	data,
	docsProposalCadence,
}: PersonalNotificationSettingsProps) {
	const zero = useZero()
	const userId = data.id

	const updateUser = async (
		values: Partial<Record<EmailNotificationField, boolean>>,
	) => {
		await mutateOk(
			zero.mutate(
				mutators.users.update({
					id: userId,
					...values,
				}),
			),
		)
		analytics.track("update-notification-settings")
	}

	const handleToggle = async (field: EmailNotificationField, value: boolean) =>
		updateUser({ [field]: value })

	const handleToggleAll = async (value: boolean) =>
		updateUser(buildToggleAllPayload(EMAIL_NOTIFICATION_FIELDS, value))

	return (
		<>
			<SettingsContainer>
				<SettingItem
					title="Email notifications"
					description={`Emails go to ${data.email}`}
					action={
						<Switch
							name="emailNotifications"
							checked={isEmailNotificationsEnabled(data)}
							onCheckedChange={handleToggleAll}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">Feedback and Threads</div>
			<SettingsContainer>
				<SettingItem
					title="New feedback"
					description="When new feedback is submitted"
					action={
						<Switch
							name="newThreadNotificationsEnabled"
							checked={data.newThreadNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("newThreadNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Productlane Agent handoffs"
					description="When the Productlane Agent tells a customer the team will follow up"
					action={
						<Switch
							name="aiHandoffNotificationsEnabled"
							checked={data.aiHandoffNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("aiHandoffNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="New replies"
					description="When someone replies to your assigned threads"
					action={
						<Switch
							name="assignedThreadsReplyReceivedNotificationsEnabled"
							checked={data.assignedThreadsReplyReceivedNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle(
									"assignedThreadsReplyReceivedNotificationsEnabled",
									value,
								)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Comment mentions"
					description="When you're mentioned in comments"
					action={
						<Switch
							name="commentMentionsNotificationsEnabled"
							checked={data.commentMentionsNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("commentMentionsNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Assignment changes"
					description="When thread assignments change"
					action={
						<Switch
							name="assignmentChangesNotificationsEnabled"
							checked={data.assignmentChangesNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle(
									"assignmentChangesNotificationsEnabled",
									value,
								)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Unassigned thread replies"
					description="When someone replies to unassigned threads"
					action={
						<Switch
							name="unassignedThreadsReplyReceivedNotificationsEnabled"
							checked={data.unassignedThreadsReplyReceivedNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle(
									"unassignedThreadsReplyReceivedNotificationsEnabled",
									value,
								)
							}}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">Content and Summaries</div>
			<SettingsContainer>
				<SettingItem
					title="New Changelog draft"
					description="When new changelog drafts are created"
					action={
						<Switch
							name="changelogEmailsEnabled"
							checked={data.changelogEmailsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("changelogEmailsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title={docsDigestToggleTitle(docsProposalCadence)}
					description="Docs your agent drafted, with accept and decline buttons in the email"
					action={
						<Switch
							name="docsProposalEmailsEnabled"
							checked={data.docsProposalEmailsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("docsProposalEmailsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Daily summary of new upvotes"
					description="A daily summary of upvotes on your portal"
					action={
						<Switch
							name="upvoteEmailsEnabled"
							checked={data.upvoteEmailsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("upvoteEmailsEnabled", value)
							}}
						/>
					}
				/>
			</SettingsContainer>
		</>
	)
}
